import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useArtistAccessSessions } from '../../../hooks/usePremiumAccess';

const { FiShield, FiX, FiRefreshCw, FiSearch, FiAlertTriangle } = FiIcons;

const SOURCE_LABELS = {
  pwyw: { label: 'PWYW', cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  subscription: { label: 'Sub', cls: 'text-green-400 bg-green-500/10 border-green-500/20' },
  manual: { label: 'Manual', cls: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  promo: { label: 'Promo', cls: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
};

const RevokeModal = ({ session, onRevoke, onClose }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRevoke = async () => {
    setLoading(true);
    await onRevoke(session.id, reason);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-950 border border-red-500/30 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-base" />
          </div>
          <div>
            <p className="text-sm font-black text-white uppercase tracking-tight">Revoke Access</p>
            <p className="text-[10px] font-bold text-zinc-500">This will immediately remove access</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Reason (optional)</label>
          <input value={reason} onChange={e => setReason(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-700"
            placeholder="e.g. Policy violation..." />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border border-zinc-700 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-500 transition-all">
            Cancel
          </button>
          <button onClick={handleRevoke} disabled={loading}
            className="flex-1 py-3 bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-black uppercase tracking-widest rounded-xl hover:bg-red-500/30 transition-all disabled:opacity-50">
            {loading ? 'Revoking...' : 'Revoke'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AccessSessionsTable = ({ artistId, contentType = 'drop' }) => {
  const { sessions, loading, revokeSession, refetch } = useArtistAccessSessions(artistId, contentType);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [revoking, setRevoking] = useState(null);

  const filtered = sessions.filter(s => {
    const matchSearch = !search || s.user_id?.toLowerCase().includes(search.toLowerCase()) || s.content_id?.toLowerCase().includes(search.toLowerCase());
    const matchSource = filterSource === 'all' || s.grant_source === filterSource;
    return matchSearch && matchSource;
  });

  const isExpired = (s) => s.expires_at && new Date(s.expires_at) < new Date();
  const isRevoked = (s) => !!s.revoked_at;

  const getStatus = (s) => {
    if (isRevoked(s)) return { label: 'Revoked', cls: 'text-red-400 bg-red-500/10 border-red-500/20' };
    if (isExpired(s)) return { label: 'Expired', cls: 'text-zinc-500 bg-zinc-800 border-zinc-700' };
    return { label: 'Active', cls: 'text-green-400 bg-green-500/10 border-green-500/20' };
  };

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            placeholder="Search by user or content ID..." />
        </div>
        <div className="flex gap-1.5">
          {['all', 'pwyw', 'subscription', 'manual', 'promo'].map(f => (
            <button key={f} onClick={() => setFilterSource(f)}
              className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                filterSource === f ? 'bg-white text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:border-zinc-700'
              }`}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={refetch} className="p-2.5 rounded-xl border border-zinc-800 text-zinc-500 hover:text-zinc-300 transition-all">
          <SafeIcon icon={FiRefreshCw} className="text-sm" />
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: sessions.length },
          { label: 'Active', value: sessions.filter(s => !isRevoked(s) && !isExpired(s)).length, accent: 'text-green-400' },
          { label: 'Expired', value: sessions.filter(s => isExpired(s) && !isRevoked(s)).length, accent: 'text-zinc-500' },
          { label: 'Revoked', value: sessions.filter(isRevoked).length, accent: 'text-red-400' },
        ].map(({ label, value, accent }) => (
          <div key={label} className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl text-center">
            <p className={`text-2xl font-black ${accent || 'text-white'}`}>{value}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-zinc-800 rounded-2xl">
          <SafeIcon icon={FiShield} className="text-3xl text-zinc-700 mx-auto mb-3" />
          <p className="text-xs font-black uppercase tracking-widest text-zinc-600">No access sessions found</p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-zinc-800 bg-zinc-900/50">
                <tr>
                  {['User ID', 'Source', 'Tier', 'Paid', 'Granted', 'Expires', 'Status', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-zinc-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {filtered.map(s => {
                  const src = SOURCE_LABELS[s.grant_source] || SOURCE_LABELS.pwyw;
                  const status = getStatus(s);
                  return (
                    <tr key={s.id} className="hover:bg-zinc-900/30 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-xs font-black text-zinc-400 font-mono">{s.user_id?.slice(0, 8)}…</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${src.cls}`}>
                          {src.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {s.d2f_access_tiers ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.d2f_access_tiers.color }} />
                            <span className="text-xs font-black text-zinc-300">{s.d2f_access_tiers.name}</span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-bold text-zinc-700">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-black text-green-400">${(s.amount_paid || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-bold text-zinc-500">
                          {new Date(s.granted_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-bold text-zinc-500">
                          {s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '∞ Never'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${status.cls}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {!isRevoked(s) && (
                          <button onClick={() => setRevoking(s)}
                            className="p-1.5 rounded-lg border border-zinc-800 text-zinc-600 hover:text-red-400 hover:border-red-500/30 transition-all">
                            <SafeIcon icon={FiX} className="text-xs" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {revoking && (
        <RevokeModal session={revoking} onRevoke={revokeSession} onClose={() => setRevoking(null)} />
      )}
    </div>
  );
};

export default AccessSessionsTable;